import React from "react";
import { connect } from "react-redux";
import { Row, FormGroup, Col, Input } from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import {
  serviceCaller,
  ServiceCallerProps,
  withModals,
} from "../../composition";
import get from "lodash.get";
import isEmpty from "lodash.isempty";
import isNil from "lodash.isnil";
import { IPost, PostType } from "../../../types/services/IPost";
import { isCurrentUserCollaborator } from "../../../utils/roleCheck";
import s from "./styles.scss";
import UrlInput from "../../../toolkit/urlInput";

const maxLabelLength = 20;

interface Props extends ServiceCallerProps {
  enablePostGiveButtonUrl: boolean;
  enablePostGiveButtonLabel: boolean;
  defaultGiveButtonLabel: string;
  canUseGiveButtonInfo: boolean;
  currentPost?: {
    post: IPost;
    type: PostType;
  };
  canEditPublishing: boolean;
  busyLoading: boolean;
  updateGiveButtonInfo: (
    giveButtonUrl: string,
    giveButtonLabel: string,
    giveButtonInfoError?: string
  ) => void;
  isVisible?: boolean;
}

interface State {
  giveButtonUrl: string;
  giveButtonLabel: string;

  giveButtonLabelCount: number;
}

class GiveButtonSection extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    const post = get(this.props.currentPost, "post", null);
    const giveButtonLabel = get(post, "giveButtonLabel", "");

    this.state = {
      giveButtonUrl: get(post, "giveButtonUrl", ""),

      giveButtonLabel,
      giveButtonLabelCount: giveButtonLabel.length,
    };
  }

  static defaultProps = {
    isVisible: true,
  };

  componentDidUpdate() {
    let giveButtonInfoError =
      this.state.giveButtonLabelCount > maxLabelLength
        ? "Give Button Label too long"
        : null;
    this.props.updateGiveButtonInfo(
      this.getUrl(),
      this.getLabel(),
      giveButtonInfoError
    );
  }

  canUseLabel = () => {
    const {
      canUseGiveButtonInfo,
      enablePostGiveButtonLabel,
      currentPost,
    } = this.props;
    const currentPostHasLabel = !isNil(
      get(currentPost, "post.giveButtonLabel", null)
    );
    const isOnholdOrDelivered = ["delivered", "onhold"].includes(
      get(currentPost, "post.status", null)
    );

    return (
      canUseGiveButtonInfo &&
      (enablePostGiveButtonLabel ||
        (currentPostHasLabel && isOnholdOrDelivered))
    );
  };

  getLabel = () => {
    return this.canUseLabel() && !isEmpty(this.state.giveButtonLabel)
      ? this.state.giveButtonLabel
      : null;
  };

  labelChangeHandler = event => {
    const label = event.target.value;
    this.setState({
      giveButtonLabel: label,
      giveButtonLabelCount: label.length,
    });
  };

  canUseUrl = () => {
    const {
      canUseGiveButtonInfo,
      enablePostGiveButtonUrl,
      currentPost,
    } = this.props;
    const currentPostHasUrl = !isNil(
      get(currentPost, "post.giveButtonUrl", null)
    );
    const isOnholdOrDelivered = ["delivered", "onhold"].includes(
      get(currentPost, "post.status", null)
    );

    return (
      canUseGiveButtonInfo &&
      (enablePostGiveButtonUrl || (currentPostHasUrl && isOnholdOrDelivered))
    );
  };

  getUrl = () => {
    return this.canUseUrl() && !isEmpty(this.state.giveButtonUrl)
      ? this.state.giveButtonUrl
      : null;
  };

  render() {
    if (!this.props.isVisible) return null;

    const {
      defaultGiveButtonLabel,
      canEditPublishing,
      busyLoading,
      currentCharity,
    } = this.props;

    const disabled =
      !canEditPublishing ||
      busyLoading ||
      !isCurrentUserCollaborator(currentCharity);

    const useLabel = this.canUseLabel(),
      useUrl = this.canUseUrl();

    if (!useLabel && !useUrl) return null;
    return (
      <Row>
        <Col lg={12} xl={{ size: 8, offset: 2 }}>
          <Row>
            {useLabel && (
              <Col sm={12} md={useUrl ? 4 : 12}>
                <FormGroup>
                  <label className={s.titleLabel} htmlFor="giveButtonLabel">
                    Button Label
                    <span
                      className={
                        this.state.giveButtonLabelCount > maxLabelLength
                          ? s.invalidContentLength
                          : s.contentLength
                      }
                    >
                      {this.state.giveButtonLabelCount} / {maxLabelLength}
                    </span>
                  </label>

                  <Input
                    type="text"
                    name="giveButtonLabel"
                    id="giveButtonLabel"
                    placeholder={
                      (defaultGiveButtonLabel
                        ? defaultGiveButtonLabel
                        : "Give Now") + " (Default)"
                    }
                    value={this.state.giveButtonLabel}
                    onChange={this.labelChangeHandler}
                    disabled={disabled}
                  />
                </FormGroup>
              </Col>
            )}

            {useUrl && (
              <Col sm={12} md={useLabel ? 8 : 12}>
                <Row className={s.urlRow}>
                  <Col xs={12}>
                    <UrlInput
                      initialUrl={get(
                        this.props.currentPost,
                        "post.giveButtonUrl",
                        null
                      )}
                      label="Button URL"
                      disabled={disabled}
                      onUrlChange={(url, _error) =>
                        this.setState({ giveButtonUrl: url })
                      }
                    />
                  </Col>
                </Row>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    );
  }
}

const mapStateToProps = state => {
  const { firebase, user } = state;
  const enablePostGiveButtonUrl = get(
    firebase,
    `data.${user.currentCharity.id}.config.enablePostGiveButtonUrl`,
    null
  );
  const enablePostGiveButtonLabel = get(
    firebase,
    `data.${user.currentCharity.id}.config.enablePostGiveButtonLabel`,
    null
  );
  const defaultGiveButtonLabel = get(
    firebase,
    `data.${user.currentCharity.id}.config.defaultGiveButtonLabel`,
    null
  );

  return {
    enablePostGiveButtonUrl,
    enablePostGiveButtonLabel,
    defaultGiveButtonLabel,
  };
};

export default serviceCaller(
  withModals(connect(mapStateToProps)(withStyles(s)(GiveButtonSection)))
);
